import { formatToBrl, secondsToTime } from "@/lib/utils";
import { VisitsAnalysisData } from "@/services/api/trackReports";
import type React from "react";
import { ReactNode } from "react";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface VisitsReportAnalysisProps {
  data: VisitsAnalysisData;
}

interface CardConfig {
  key: keyof VisitsAnalysisData["analysis_data"];
  title: string;
  formatter?: (value: string | number) => ReactNode;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number | string; name: string }>;
  label?: string;
}

const VisitsReportAnalysis = ({ data }: VisitsReportAnalysisProps) => {
  const CustomTooltip: React.FC<CustomTooltipProps> = ({
    active,
    payload,
    label,
  }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-700 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-600 p-2 rounded shadow-md">
          <p className="font-semibold m-0">{`Cliente: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} className="m-0">
              {entry.name === "Tempo total"
                ? `${entry.name}: ${secondsToTime(entry.value as number)}`
                : `${entry.name}: ${entry.value} visitas`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomTooltipInterval: React.FC<CustomTooltipProps> = ({
    active,
    payload,
    label,
  }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-700 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-600 p-2 rounded shadow-md">
          <p className="font-semibold m-0">{`Cliente: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} className="m-0">
              {`${entry.name}: ${entry.value} dia(s)`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const cardConfigs: CardConfig[] = [
    {
      key: "total_visits",
      title: "Total de visitas",
    },
    {
      key: "total_clients",
      title: "Total de clientes",
    },
    {
      key: "average_visits_per_day",
      title: "Média de visitas por dia",
      formatter: (value) => formatToBrl(value as number, 1),
    },
    {
      key: "average_standing_time",
      title: "Tempo médio de permanência",
    },
    {
      key: "km_travelled_between_visit",
      title: "Distância média entre visitas",
      formatter: (value) => `${formatToBrl(value as number, 1)} km`,
    },
    {
      key: "time_between_visit",
      title: "Tempo médio entre visitas",
    },
  ];

  const formatValue = (config: CardConfig) => {
    const value = data.analysis_data[config.key];
    return config.formatter ? config.formatter(value) : value;
  };

  const visitedClientsChartData = data.chart_data[0];
  const visitsIntervalChartData = data.chart_data[1];

  return (
    <div>
      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-4 min-h-[140px] relative z-10">
        {cardConfigs.map((config) => (
          <div
            key={config.key}
            className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border border-slate-100 dark:border-slate-700 h-full relative z-10"
          >
            <div className="h-full flex flex-col">
              <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1">
                {config.title}
              </h3>
              <div className="text-3xl font-bold text-slate-800 dark:text-white my-auto grow flex items-center">
                {formatValue(config)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart - Clientes visitados */}
      <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border border-slate-100 dark:border-slate-700 relative z-10 mb-4">
        <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1">
          Clientes visitados
        </h3>
        {/* Barchart */}
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={visitedClientsChartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
          >
            {/* Eixo X com formatação de rótulos */}
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
              tickFormatter={(label) =>
                label.length > 12 ? `${label.substring(0, 12)}...` : label
              }
            />

            {/* Eixo Y esquerdo */}
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `${value} visitas`}
            />

            {/* Eixo Y direito */}
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `${secondsToTime(value)}h`}
            />

            {/* Tooltip personalizado */}
            <Tooltip content={<CustomTooltip />} />

            {/* Barras */}
            <Bar
              yAxisId="left"
              dataKey="count"
              fill="#307089"
              name="Número de visitas"
            />
            <Bar
              yAxisId="right"
              dataKey="time"
              fill="#68b3d1"
              name="Tempo total"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Chart - Retorno das visitas */}
      <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border border-slate-100 dark:border-slate-700 relative z-10 mb-4">
        <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1">
          Retorno das visitas
        </h3>
        {/* Barchart */}
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={visitsIntervalChartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
          >
            {/* Eixo X com formatação de rótulos */}
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
              tickFormatter={(label) =>
                label.length > 12 ? `${label.substring(0, 12)}...` : label
              }
            />

            {/* Eixo Y esquerdo */}
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `${value}d`}
            />

            {/* Eixo Y direito */}
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `${value}d`}
            />

            {/* Tooltip personalizado */}
            <Tooltip content={<CustomTooltipInterval />} />

            {/* Barras */}
            <Bar
              yAxisId="left"
              dataKey="average_interval"
              fill="#307089"
              name="Tempo médio retorno"
            />
            <Bar
              yAxisId="right"
              dataKey="longest_interval"
              fill="#68b3d1"
              name="Maior intervalo sem visitas"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default VisitsReportAnalysis;
